import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dealService } from '@/api/services/DealService';
import { useAuth } from '@/stores/authContext';
import { useIsAgent } from '@/hooks/useRoles';
import type { DealWithSide } from '@/lib/dashboard';
import type { DealOrderType } from '@/types/deals';

// Сводка сделок для «Главной»: только АКТИВНЫЕ сделки (completed=false), обе стороны, первая страница.
// Завершённые и отменённые «Главной» не нужны: дел по ним нет, а в сумму панели они попасть не должны.
// Поэтому total — точное число активных сделок, а выборка неполна, только если у стороны их больше лимита.
// Ключ начинается с 'bargains' → realtime-инвалидация по SSE (dealUpdate) обновляет и дашборд.
const OVERVIEW_LIMIT = 50;

function useOverviewSide(orderType: DealOrderType, enabled: boolean) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['bargains', 'overview', orderType, 'active'] as const,
    queryFn: ({ signal }) =>
      dealService.getMyBargains({ orderType, completed: false, page: 1, limit: OVERVIEW_LIMIT }, signal),
    enabled: isAuthenticated && enabled,
    staleTime: 30_000,
  });
}

// Агент — посредник во всех сделках, стороны у него нет: тянем ЕДИНУЮ выборку без orderType.
// Иначе SALE и PURCHASE вернули бы один и тот же набор (бэкенд для роли AGENT игнорирует orderType),
// и сделки задвоились бы в сводке (счётчик плитки и лента).
function useAgentOverview(enabled: boolean) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['bargains', 'overview', 'agent', 'active'] as const,
    queryFn: ({ signal }) => dealService.getMyBargains({ completed: false, page: 1, limit: OVERVIEW_LIMIT }, signal),
    enabled: isAuthenticated && enabled,
    staleTime: 30_000,
  });
}

/** Число сделок одной стороны: страница из одной сделки, нужен только {@code total} ответа. */
function useSideTotal(orderType: DealOrderType, enabled: boolean) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    // Ключ начинается с 'bargains' → SSE-обновление сделок (dealUpdate) пересчитывает и счётчики.
    queryKey: ['bargains', 'sideTotal', orderType] as const,
    queryFn: ({ signal }) => dealService.getMyBargains({ orderType, page: 1, limit: 1 }, signal),
    enabled: isAuthenticated && enabled,
    staleTime: 30_000,
  });
}

/**
 * Общее число сделок по сторонам — для счётчиков вкладок «Продажи / Покупки». Лёгкие запросы (limit 1), а не
 * сводка «Главной» на 50 сделок: из ответа нужно только общее число. Пока сторона грузится или её запрос упал —
 * {@code undefined}: неизвестное число не показываем. Агенту не нужен (у него вкладки по статусу).
 */
export function useDealSideTotals(): { readonly sale?: number; readonly purchase?: number } {
  const isAgent = useIsAgent();
  const sale = useSideTotal('SALE', !isAgent);
  const purchase = useSideTotal('PURCHASE', !isAgent);
  return { sale: sale.data?.total, purchase: purchase.data?.total };
}

export interface DealsOverview {
  readonly deals: DealWithSide[];
  /** Число активных сделок по ответу сервера, даже если загружена только первая страница. */
  readonly total: number;
  readonly isLoading: boolean;
  /** Ошибка только если упали ОБЕ вкладки (частичную сводку всё равно показываем). */
  readonly isError: boolean;
  /** Упала РОВНО ОДНА вкладка: часть сделок отсутствует, а {@code total} занижен — счётчику доверять нельзя
   *  (для агента всегда false: у него единый запрос без сторон). */
  readonly partial: boolean;
  /** Загружены не все активные сделки (их больше лимита): сделка может остаться за пределами сводки,
   *  поэтому утверждать «срочных дел нет», считать решения и суммы нельзя. */
  readonly truncated: boolean;
  /** Идёт повторный запрос. */
  readonly isRefetching: boolean;
  /** Повторить запросы, завершившиеся ошибкой. */
  readonly retry: () => void;
}

export function useDealsOverview(): DealsOverview {
  const isAgent = useIsAgent();
  // Все хуки вызываем безусловно (правила хуков); лишние гасим через enabled.
  const sale = useOverviewSide('SALE', !isAgent);
  const purchase = useOverviewSide('PURCHASE', !isAgent);
  const agent = useAgentOverview(isAgent);

  return useMemo(() => {
    if (isAgent) {
      const items = agent.data?.bargains ?? [];
      // side у агента смысла не несёт (он посредник); ставим 'SALE'-заглушку. Лента «внимание» считается
      // с isAgent=true и side не использует (см. buildAttention).
      const deals: DealWithSide[] = items.map((item) => ({ side: 'SALE' as const, item }));
      const total = agent.data?.total ?? 0;
      // Сбой фонового обновления при данных в кэше — не отсутствие данных: кэш показываем.
      const agentFailed = agent.isError && agent.data == null;
      return {
        deals,
        total,
        isLoading: agent.isLoading,
        isError: agentFailed,
        partial: false,
        truncated: deals.length < total,
        isRefetching: agent.isRefetching,
        retry: () => void agent.refetch(),
      };
    }
    const saleItems = sale.data?.bargains ?? [];
    const purchaseItems = purchase.data?.bargains ?? [];
    const deals: DealWithSide[] = [
      ...saleItems.map((item) => ({ side: 'SALE' as const, item })),
      ...purchaseItems.map((item) => ({ side: 'PURCHASE' as const, item })),
    ];
    const total = (sale.data?.total ?? 0) + (purchase.data?.total ?? 0);
    const saleFailed = sale.isError && sale.data == null;
    const purchaseFailed = purchase.isError && purchase.data == null;
    return {
      deals,
      total,
      isLoading: sale.isLoading || purchase.isLoading,
      isError: saleFailed && purchaseFailed,
      // Упала ровно одна вкладка: показываем частичные сделки, но счётчик занижен → помечаем недостоверным.
      partial: saleFailed !== purchaseFailed,
      truncated: deals.length < total,
      isRefetching: sale.isRefetching || purchase.isRefetching,
      retry: () => {
        if (sale.isError) {
          void sale.refetch();
        }
        if (purchase.isError) {
          void purchase.refetch();
        }
      },
    };
  }, [
    isAgent,
    agent.data,
    agent.isLoading,
    agent.isError,
    agent.isRefetching,
    agent.refetch,
    sale.isRefetching,
    sale.refetch,
    purchase.isRefetching,
    purchase.refetch,
    sale.data,
    purchase.data,
    sale.isLoading,
    purchase.isLoading,
    sale.isError,
    purchase.isError,
  ]);
}
